import React, { useState, useEffect, useMemo } from 'react';
import { BaseCard, type BaseCardData } from './BaseCard';
import { CHART_STYLES } from '../constants/chartStyles';
import { handlePieChartClick } from '../utils/analyticsRedirect';
import {
  AnalyticsMetric,
  ClickablePieChartMetrics,
  MetricsWithoutChartButton,
} from '../const';
import { MetricSwitcher } from './MetricSwitcher';
import { MetricsWithTags } from '../../conversations/const';
import { UnifiedPieChart } from './UnifiedPieChart';
import type { PieChartTypeValue } from '../types';

export type PieChartData = BaseCardData & {
  data: Array<{
    name: string;
    value: number;
    color: string;
    originalValue?: number;
    percentage?: number;
    selectedConversations?: number;
  }>;
  unit?: string;
  type: PieChartTypeValue;
  totalSum?: number;
  numberOfConversations?: number;
  seriesData?: Array<{
    bucket: string;
    value: string;
    conversationNumber?: number | null;
  }>;
  scale?: string | null;
};

type PieChartCardProps = {
  item: PieChartData;
  onClick?: (item: PieChartData) => void;
  onChartClick?: (item: PieChartData) => void;
  height?: string;
  agentId?: string;
  period?: string;
  alternativeMetric?: PieChartData;
};

export const PieChartCard: React.FC<PieChartCardProps> = ({
  item,
  onClick,
  onChartClick,
  height = 'h-[240px]',
  agentId,
  period,
  alternativeMetric,
}) => {
  const [currentMetric, setCurrentMetric] = useState<PieChartData>(item);

  useEffect(() => {
    setCurrentMetric(item);
  }, [item]);

  const handleToggle = () => {
    if (!alternativeMetric) return;

    setCurrentMetric(prevMetric => {
      if (prevMetric.type === item.type) {
        return alternativeMetric;
      } else {
        return item;
      }
    });
  };

  const hasToggle =
    alternativeMetric &&
    MetricsWithTags.includes(item.type) &&
    MetricsWithTags.includes(alternativeMetric.type);

  const displayItem = hasToggle ? currentMetric : item;

  const title = useMemo(() => {
    switch (displayItem.type) {
      case AnalyticsMetric.MOOD_TAGS:
        return 'Sentiment';
      case AnalyticsMetric.TOPIC_TAGS:
        return 'Category';
      default:
        return '';
    }
  }, [displayItem.type]);

  if (!displayItem.data || displayItem.data.length === 0) {
    return (
      <BaseCard<PieChartData>
        item={displayItem}
        onClick={onClick}
        showChartButton={
          !!(
            displayItem.seriesData &&
            displayItem.seriesData.length > 0 &&
            !MetricsWithoutChartButton.includes(displayItem.type)
          )
        }
        onChartClick={onChartClick}
        className={`w-full ${height} flex flex-col bg-white rounded-card cursor-pointer p-3 sm:p-4 lg:p-5 xl:p-6 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:outline-none min-w-0`}
        iconSize="small"
        headerClassName="flex justify-between items-start mb-2 sm:mb-3 lg:mb-4"
        contentClassName="flex-1 flex items-center justify-center relative"
        customHeaderActions={
          hasToggle ? (
            <MetricSwitcher title={title} onToggle={handleToggle} />
          ) : undefined
        }
      >
        <div className="flex items-center justify-center h-full text-secondary">
          No data available
        </div>
      </BaseCard>
    );
  }

  const getChartProps = () => {
    switch (displayItem.type) {
      case AnalyticsMetric.SYSTEM_AVAILABILITY:
        return {
          showLegend: false,
          showCenterValue: false,
          filterEmptyEntries: false,
          innerRadius: CHART_STYLES.DIMENSIONS.PIE_INNER_RADIUS,
          outerRadius: CHART_STYLES.DIMENSIONS.PIE_OUTER_RADIUS,
        };
      case AnalyticsMetric.TOPIC_TAGS:
      case AnalyticsMetric.MOOD_TAGS:
        return {
          showLegend: true,
          showCenterValue: false,
          filterEmptyEntries: true,
          innerRadius: CHART_STYLES.DIMENSIONS.PIE_TOPIC_INNER_RADIUS,
          outerRadius: CHART_STYLES.DIMENSIONS.PIE_TOPIC_OUTER_RADIUS,
        };
      default:
        return {
          showLegend: false,
          showCenterValue: true,
          filterEmptyEntries: false,
          innerRadius: CHART_STYLES.DIMENSIONS.PIE_INNER_RADIUS,
          outerRadius: CHART_STYLES.DIMENSIONS.PIE_OUTER_RADIUS,
        };
    }
  };

  const chartProps = getChartProps();

  return (
    <BaseCard<PieChartData>
      item={displayItem}
      onClick={onClick}
      onChartClick={onChartClick}
      showChartButton={
        !!(
          displayItem.seriesData &&
          displayItem.seriesData.length > 0 &&
          !MetricsWithoutChartButton.includes(displayItem.type)
        )
      }
      className={`w-full ${height} flex flex-col bg-white rounded-card cursor-pointer p-3 sm:p-4 lg:p-5 xl:p-6 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:outline-none min-w-0`}
      iconSize="small"
      headerClassName="flex justify-between items-start mb-2 sm:mb-3 lg:mb-4"
      contentClassName="flex-1 flex items-center justify-center relative"
      customHeaderActions={
        hasToggle ? (
          <MetricSwitcher title={title} onToggle={handleToggle} />
        ) : undefined
      }
    >
      <UnifiedPieChart
        data={displayItem.data}
        type={displayItem.type}
        totalSum={displayItem.totalSum}
        numberOfConversations={displayItem.numberOfConversations}
        onSegmentClick={segmentName => {
          if (
            agentId &&
            period &&
            ClickablePieChartMetrics.includes(displayItem.type)
          ) {
            handlePieChartClick(
              displayItem.type,
              segmentName,
              agentId,
              period as any
            );
          }
        }}
        {...chartProps}
      />
    </BaseCard>
  );
};
